import React, { useState } from 'react';
import { Button, Modal, Select, Tabs, Upload, message, notification } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { makePostCall, getUserDetails } from '../utils/helper';

const { TabPane } = Tabs;

const DEFAULT_FILE_TYPE_OPTIONS = [
  { label: 'Prescription', value: 'prescription' },
  { label: 'Consent', value: 'consent' },
  { label: 'Referral Form', value: 'referral_form' },
  { label: 'Others', value: 'others' },
];

// Self-contained "Add Notes / Upload File" dialog. All draft text/file state is
// kept LOCAL here so typing only re-renders this small component, not the large
// host page (the previous inline version called the page's setState per keystroke,
// causing typing lag).
const AddFileModal = ({
  open,
  data,
  initialType = 'upload',
  onClose,
  onUploaded,
  title,
  fileTypeOptions = DEFAULT_FILE_TYPE_OPTIONS,
}) => {
  const [modalType, setModalType] = useState(initialType);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<any>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const isNotes = modalType === 'notes';

  const handleUpload = async () => {
    const pacs_order = data?.pacs_order;

    if (!isNotes && file?.size / 1024 > 500) {
      message.error('File size should be less than 500kb', 5);
      return;
    }

    const formData = new FormData();
    formData.append('order_ids', pacs_order?.pacs_ord_id);
    formData.append('order_id', pacs_order?.pacs_ord_id);
    formData.append('pin', pacs_order?.patient?.pat_pin);

    if (isNotes) {
      formData.append('type', 'notes');
      formData.append('notes', notes);
    } else {
      formData.append('file_type', fileType);
      formData.append('file', file);
    }
    formData.append('user_id', getUserDetails()?.username);

    const url = isNotes ? '/upload-notes' : '/upload-file';
    const headers = { 'Content-Type': 'multipart/form-data' };

    setUploading(true);
    try {
      await makePostCall(url, formData, { headers });
      notification.success({
        message: 'Success',
        description: 'File uploaded successfully',
      });
      if (onUploaded) onUploaded();
      onClose();
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      console.log(msg);
      notification.error({
        message: 'Error',
        description: `Error while uploading file [${msg}]`,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onOk={handleUpload}
      confirmLoading={uploading}
      onCancel={onClose}
      okButtonProps={{
        disabled: (isNotes && !notes) || (!isNotes && !file),
      }}
    >
      <Tabs activeKey={modalType} onTabClick={key => setModalType(key)}>
        <TabPane key="notes" tab="Notes">
          <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ fontWeight: 500, fontSize: '1rem' }}>Enter Notes / Remarks</label>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                Add any additional notes or remarks about this study
              </p>
            </div>
            <TextArea
              placeholder="Type your notes here..."
              rows={6}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{
                fontSize: '0.9rem',
                borderRadius: '0.5rem',
                border: '1px solid #d9d9d9',
              }}
            />
          </div>
        </TabPane>
        <TabPane key="upload" tab="File Upload">
          <div style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <label className="file-box-text">Select File Type</label>
              <Select
                placeholder="Choose file type"
                options={fileTypeOptions}
                value={fileType}
                style={{ width: '100%' }}
                onChange={val => setFileType(val)}
              />
            </div>
            <div>
              <label className="file-box-text">Upload PDF File</label>
              <div className={`upload-pdf-box ${fileType ? 'active' : ''}`}>
                <Upload
                  customRequest={({ file: selectedFile }) => setFile(selectedFile)}
                  accept="application/pdf"
                  multiple={false}
                  disabled={!fileType}
                  showUploadList={false}
                >
                  <Button
                    disabled={!fileType}
                    className={`upload-pdf-button ${!fileType ? 'active' : ''}`}
                  >
                    📁 Select File
                  </Button>
                </Upload>
                <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666' }}>
                  {file ? (
                    <span style={{ color: '#00795C', fontWeight: 500 }}>✓ {file.name}</span>
                  ) : (
                    <>
                      <span>Max file size: 500 KB</span>
                      <br />
                      <span>Supported: PDF only</span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default AddFileModal;
